<script setup lang="ts">
/**
 * El único snackbar de la app, montado en `App.vue` para que cubra todos los
 * layouts (el autenticado, el de login y el de error).
 *
 * `VSnackbarQueue` encola: dos guardados seguidos se muestran uno tras otro en
 * vez de pisarse.
 *
 * El ícono va en una caja clara sobre el color del aviso —el patrón de toast de
 * la plantilla—, así el resultado se lee de un vistazo sin depender solo del
 * color. La cruz cierra sin esperar el temporizador.
 */
import { snackbarQueue } from '@/composables/useSnackbar'

const ICONS: Record<string, string> = {
  success: 'ri-checkbox-circle-line',
  error: 'ri-error-warning-line',
  warning: 'ri-alert-line',
  info: 'ri-information-line',
}

const iconFor = (color?: string) => ICONS[color ?? 'info'] ?? ICONS.info
</script>

<template>
  <!--
    El `timeout` sale del aviso que se está mostrando —el primero de la cola—
    porque `VSnackbarQueue` tiene un único temporizador para todos. Así un aviso
    de contexto puede quedarse veinte segundos sin que los "Guardado" duren lo
    mismo.
  -->
  <VSnackbarQueue
    v-model="snackbarQueue"
    location="top right"
    :timeout="snackbarQueue[0]?.timeout ?? 5000"
  >
    <template #text="{ item }">
      <div class="d-flex align-center gap-x-3">
        <VAvatar
          :size="34"
          rounded="lg"
          class="bt-snackbar-icon"
        >
          <VIcon
            :icon="iconFor(item.color)"
            :color="item.color"
            size="22"
          />
        </VAvatar>
        <!-- Sin clase de tipografía: `text-body-1` de la plantilla trae su
             propio color (`on-surface` con opacidad), y sobre el fondo del
             snackbar el texto salía gris oscuro en claro y gris en oscuro. Así
             hereda el color de contraste que ya calcula `VSnackbar`. -->
        <span>{{ item.text }}</span>
      </div>
    </template>

    <template #actions="{ props: closeProps }">
      <VBtn
        icon="ri-close-line"
        variant="text"
        color="default"
        size="small"
        v-bind="closeProps"
      />
    </template>
  </VSnackbarQueue>
</template>

<style scoped>
/*
 * Caja del ícono: blanco translúcido para que funcione sobre verde, rojo y
 * ámbar sin una variante por color.
 */
.bt-snackbar-icon {
  background: rgba(255, 255, 255, 92%);
}
</style>
